import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmgAuthClaims } from '@/auth/authClient';
import { CopyableValue } from './CopyableValue';

export interface ProfileInfoTabProps {
    claims: EmgAuthClaims;
    displayName: string;
    /** "role,role · org name" from real `/me/access` data — see ProfilePage.tsx. */
    roleLine?: string;
    onEditProfile: () => void;
    onOpenPasswordModal: () => void;
}

/** A read-only info-grid row — frame 77's `180px label / value` two-column table. */
function InfoRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="portal-profile-info-row">
            <span className="portal-profile-info-label">{label}</span>
            <span className="portal-profile-info-value">{children}</span>
        </div>
    );
}

/**
 * `/profile` — "Thông tin" tab (frame 77).
 *
 * EAA-1820: identity header + two read-only cards ("Thông tin cá nhân",
 * "Định danh đăng nhập") + a "Mật khẩu" card. The frame's personal-info card
 * shows phone/birthday/gender/address as real values, but D10 (`emg_shared`
 * kernel purity) pins the identity token to sub/email/name/sid ONLY, and
 * `profileFormState.ts` already established (EAA-1822) that no portal-api
 * model persists any of those fields — so every row this session cannot back
 * with real data renders `profile.infoTab.noData` rather than the frame's
 * literal sample value. Giới tính's amber "Chưa chọn" state is real here
 * (genuinely unset, not fabricated) and happens to match the frame exactly.
 *
 * Same honesty rule applies to "Định danh đăng nhập": `EmgAuthClaims` has no
 * username/realm/created-at field (never has — see `authClient.ts`), so only
 * "Mã sub" is real; "Tên đăng nhập" / "Realm" / "Tạo lúc" render as
 * unavailable rather than parsed out of the JWT behind the kernel's back.
 * "Đổi lần cuối" on the Mật khẩu card is the same gap — Keycloak's token
 * carries no password-changed-at claim.
 */
export function ProfileInfoTab({
    claims,
    displayName,
    roleLine,
    onEditProfile,
    onOpenPasswordModal,
}: ProfileInfoTabProps) {
    const { t } = useTranslation();
    const initial = displayName.trim().charAt(0).toUpperCase() || '?';
    const noData = t('profile.infoTab.noData');

    return (
        <>
            <div className="portal-profile-card portal-profile-identity-card">
                <span className="portal-profile-avatar" aria-hidden="true">
                    {initial}
                </span>
                <div className="portal-profile-identity-body">
                    <span className="portal-profile-identity-name">
                        {claims.name ?? claims.email ?? t('profile.defaultUserName')}
                    </span>
                    {roleLine && <span className="portal-profile-edit-role-line">{roleLine}</span>}
                    <span className="portal-profile-status-row">
                        <span className="portal-profile-status-dot" aria-hidden="true" />
                        {t('profile.activeStatus')}
                    </span>
                </div>
                <button type="button" className="portal-btn portal-btn-primary" onClick={onEditProfile}>
                    {t('profile.editProfile')}
                </button>
            </div>

            <div className="portal-profile-info-grid">
                <div className="portal-profile-card portal-profile-card-flush">
                    <div className="portal-profile-info-card-header">
                        <span className="portal-profile-info-card-heading">
                            <span className="portal-profile-info-card-title">
                                {t('profile.infoTab.personalCardTitle')}
                            </span>
                            <span className="portal-profile-info-card-desc">
                                {t('profile.infoTab.personalCardDesc')}
                            </span>
                        </span>
                        <button type="button" className="portal-btn" onClick={onEditProfile}>
                            {t('profile.infoTab.editAction')}
                        </button>
                    </div>
                    <div className="portal-profile-info-table">
                        <InfoRow label={t('profile.infoTab.rowFullName')}>{displayName}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowEmail')}>
                            {claims.email ?? noData}
                            <span className="portal-profile-info-hint">
                                {t('profile.infoTab.rowEmailHelper')}
                            </span>
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowPhone')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowBirthday')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowGender')}>
                            <span className="portal-profile-info-warn">
                                {t('profile.infoTab.rowGenderUnset')}
                            </span>
                            <span className="portal-profile-info-hint">
                                {t('profile.infoTab.rowGenderHelper')}
                            </span>
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowAddress')}>{noData}</InfoRow>
                    </div>
                </div>

                <div className="portal-profile-card portal-profile-card-flush">
                    <div className="portal-profile-info-card-header">
                        <span className="portal-profile-info-card-heading">
                            <span className="portal-profile-info-card-title">
                                {t('profile.infoTab.identityCardTitle')}
                            </span>
                            <span className="portal-profile-info-card-desc">
                                {t('profile.infoTab.identityCardDesc')}
                            </span>
                        </span>
                    </div>
                    <div className="portal-profile-info-table">
                        <InfoRow label={t('profile.infoTab.rowUsername')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowSub')}>
                            <CopyableValue value={claims.sub} ariaLabel={t('profile.subLabel')} />
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowRealm')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowCreatedAt')}>{noData}</InfoRow>
                    </div>
                </div>
            </div>

            <div className="portal-profile-card portal-profile-password-card">
                <div className="portal-profile-info-card-heading">
                    <span className="portal-profile-info-card-title">
                        {t('profile.infoTab.passwordCardTitle')}
                    </span>
                    <span className="portal-profile-info-card-desc">
                        {t('profile.infoTab.passwordCardDesc')}
                    </span>
                </div>
                <div className="portal-profile-password-card-row">
                    <span className="portal-profile-password-last-changed">
                        {t('profile.infoTab.passwordLastChangedUnknown')}
                    </span>
                    <button type="button" className="portal-btn" onClick={onOpenPasswordModal}>
                        {t('profile.changePassword')}
                    </button>
                </div>
            </div>
        </>
    );
}

export default ProfileInfoTab;
